import { createRoute, Link, useNavigate, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { Shell } from '../../components/layout/Shell';
import { ItemForm } from '../../components/inventory/ItemForm';
import { ImageEditor } from '../../components/inventory/ImageEditor';
import { ImageGallery } from '../../components/inventory/ImageGallery';
import { ImageLightbox } from '../../components/inventory/ImageLightbox';
import { AiReviewModal } from '../../components/inventory/AiReviewModal';
import { ItemLinks } from '../../components/inventory/ItemLinks';
import { CapacitorList } from '../../components/capacitor/CapacitorList';
import { RetroBrightSection } from '../../components/retrobright/RetroBrightSection';
import { AlertList } from '../../components/health/AlertList';
import { ListingStatusSelector } from '../../components/community/ListingStatusSelector';
import { CreateOfferForm } from '../../components/community/CreateOfferForm';
import { FavoriteButton } from '../../components/community/FavoriteButton';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { useToast } from '../../components/ui/Toast';
import { useAuth } from '../../hooks/useAuth';
import { useAppMode } from '../../context/AppModeContext';
import { usePlatformLabel } from '../../hooks/usePlatformLabel';
import {
  useInventoryItem,
  useInventoryList,
  useUpdateInventoryItem,
  useDeleteInventoryItem,
  useToggleShowroom,
  useAnalyzeImage,
} from '../../hooks/useInventory';
import {
  useItemImages,
  useUploadItemImage as useUploadItemImageMulti,
  useDeleteItemImage,
  useSetPrimaryImage,
} from '../../hooks/useItemImages';
import { useCapacitorMap } from '../../hooks/useCapacitorMap';
import { useItemFiles } from '../../hooks/useLibrary';
import { apiClient } from '../../api/client';
import { Route as rootRoute } from '../__root';
import type { CreateInventoryItemInput, UpdateInventoryItemInput } from '@siliconvault/shared';
import { useTranslation } from '../../i18n';
import styles from './$itemId.module.css';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/inventory/$itemId',
  validateSearch: (search: Record<string, unknown>) => ({
    from: (search.from as string) || undefined,
  }),
  component: ItemDetailPage,
});

function ItemDetailPage() {
  const { itemId } = useParams({ from: '/inventory/$itemId' });
  const { from } = Route.useSearch();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const { mode, hasAiAccess } = useAppMode();
  const { data: item, isLoading } = useInventoryItem(itemId);
  const updateMutation = useUpdateInventoryItem();
  const deleteMutation = useDeleteInventoryItem();
  const showcaseMutation = useToggleShowroom();
  const analyzeMutation = useAnalyzeImage();
  const { data: imagesData } = useItemImages(itemId);
  const uploadMultiMutation = useUploadItemImageMulti();
  const deleteImageMutation = useDeleteItemImage();
  const setPrimaryMutation = useSetPrimaryImage();
  const { data: capMapData } = useCapacitorMap(itemId);
  const { data: filesData } = useItemFiles(itemId);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [ownerListedItems, setOwnerListedItems] = useState<import('@siliconvault/shared').InventoryItem[]>([]);
  const [editorImageSrc, setEditorImageSrc] = useState<string | null>(null);
  const [editorImageId, setEditorImageId] = useState<string | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<any>(null);
  const { t } = useTranslation();
  const platformLabel = usePlatformLabel();

  // Fetch own items for offering (only when offer modal opens)
  const { data: ownItemsData } = useInventoryList(showOfferModal ? { limit: 100 } : undefined);

  const backTo = from === 'showroom' ? '/showroom' : '/inventory';
  const backLabel = from === 'showroom' ? t('itemDetail.backToShowroom') : t('itemDetail.backToInventory');

  if (isLoading) {
    return (
      <Shell>
        <div>{t('itemDetail.loadingItem')}</div>
      </Shell>
    );
  }

  if (!item) {
    return (
      <Shell>
        <div>{t('itemDetail.itemNotFound')}</div>
        <Link to="/inventory">{t('itemDetail.backToInventory')}</Link>
      </Shell>
    );
  }

  const isOwner = item.createdBy === user?.userId;
  const canEdit = user?.role === 'admin' || isOwner;

  async function handleUpdate(data: CreateInventoryItemInput) {
    try {
      await updateMutation.mutateAsync({ itemId, data });
      setIsEditing(false);
      toast('Item updated successfully', 'success');
    } catch {
      toast('Failed to update item', 'error');
    }
  }

  async function handleDelete() {
    try {
      await deleteMutation.mutateAsync(itemId);
      toast('Item deleted', 'success');
      navigate({ to: '/inventory' });
    } catch {
      toast('Failed to delete item', 'error');
    }
  }

  async function handleEditorSave(blob: Blob) {
    try {
      // Delete the old image, then upload the replacement
      if (editorImageId) {
        await deleteImageMutation.mutateAsync({ itemId, imageId: editorImageId });
      }
      await uploadMultiMutation.mutateAsync({ itemId, file: blob });
      setEditorImageSrc(null);
      setEditorImageId(null);
      toast('Image saved', 'success');
    } catch {
      toast('Failed to save image', 'error');
    }
  }

  function handleEditorCancel() {
    setEditorImageSrc(null);
    setEditorImageId(null);
  }

  async function handleReanalyze() {
    if (!item?.imageUrl) return;
    try {
      const response = await fetch(item.imageUrl);
      if (!response.ok) throw new Error('Failed to fetch image');
      const blob = await response.blob();
      const result = await analyzeMutation.mutateAsync(blob);
      setAnalysisResult(result);
      setShowReviewModal(true);
    } catch {
      toast(t('itemDetail.reanalyzeFailed'), 'error');
    }
  }

  async function handleAcceptAnalysis(data: UpdateInventoryItemInput) {
    try {
      await updateMutation.mutateAsync({ itemId, data });
      setShowReviewModal(false);
      setAnalysisResult(null);
      toast(t('itemDetail.reanalyzeApplied'), 'success');
    } catch {
      toast('Failed to update item', 'error');
    }
  }

  if (isEditing) {
    return (
      <Shell>
        <div className={styles.page}>
          <Link to={backTo} className={styles.backLink}>
            &larr; {backLabel}
          </Link>
          <h1 className={styles.title}>{t('itemDetail.editItem')}</h1>
          <ItemForm
            initialData={{
              name: item.name,
              category: item.category,
              platform: item.platform,
              condition: item.condition as CreateInventoryItemInput['condition'],
              quantity: item.quantity,
              description: item.description,
              acquisitionDate: item.acquisitionDate,
              acquisitionPrice: item.acquisitionPrice,
              currency: item.currency as CreateInventoryItemInput['currency'],
              subcategory: item.subcategory,
              location: item.location,
              metadata: (item as any).metadata ?? {},
            }}
            onSubmit={handleUpdate}
            onCancel={() => setIsEditing(false)}
            isLoading={updateMutation.isPending}
            submitLabel={t('itemForm.updateItem')}
          />
        </div>
      </Shell>
    );
  }

  const files = filesData?.data ?? [];

  return (
    <Shell>
      <div className={styles.page} style={{ maxWidth: '1000px' }}>
        <Link to={backTo} className={styles.backLink}>
          &larr; {backLabel}
        </Link>

        <div className={styles.header}>
          <h1 className={styles.title}>{item.name}</h1>
          {canEdit && (
            <div className={styles.actions}>
              {mode !== 'desktop' && (
                <Button
                  size="small"
                  variant={item.showcase ? 'secondary' : 'primary'}
                  onClick={() => showcaseMutation.mutate({ itemId, showcase: !item.showcase })}
                  disabled={showcaseMutation.isPending}
                >
                  {item.showcase ? t('itemDetail.removeFromShowroom') : t('itemDetail.showroom')}
                </Button>
              )}
              <Button size="small" onClick={() => setIsEditing(true)}>
                {t('itemDetail.edit')}
              </Button>
              <Button
                size="small"
                variant="danger"
                onClick={() => setShowDeleteModal(true)}
              >
                {t('itemDetail.delete')}
              </Button>
            </div>
          )}
        </div>

        <div className={styles.content}>
          <ImageGallery
            itemId={itemId}
            images={imagesData?.data ?? []}
            canEdit={canEdit}
            onZoom={(url) => {
              const images = imagesData?.data ?? [];
              const idx = images.findIndex((img) => img.imageUrl === url);
              setLightboxIndex(idx >= 0 ? idx : 0);
            }}
            onUpload={(files) => {
              for (const file of files) {
                uploadMultiMutation.mutate({ itemId, file }, {
                  onError: () => toast(t('itemDetail.imageUploadFailed'), 'error'),
                });
              }
              if (files.length > 0) {
                toast(files.length === 1 ? t('itemDetail.imageSaved') : `${files.length} images uploading...`, 'success');
              }
            }}
            onDelete={(imageId) => {
              deleteImageMutation.mutate({ itemId, imageId }, {
                onSuccess: () => toast(t('imageGallery.deleteImage'), 'success'),
                onError: () => toast(t('itemDetail.imageUploadFailed'), 'error'),
              });
            }}
            onSetPrimary={(imageId) => {
              setPrimaryMutation.mutate({ itemId, imageId });
            }}
            onEditImage={(url, imageId) => { setEditorImageSrc(url); setEditorImageId(imageId); }}
            isUploading={uploadMultiMutation.isPending}
            aiConfigured={hasAiAccess}
            onReanalyze={handleReanalyze}
            isReanalyzing={analyzeMutation.isPending}
          />

          <div className={styles.details}>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>{platformLabel}</span>
              <span className={styles.detailValue}>{item.platform}</span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>{t('itemDetail.category')}</span>
              <span className={styles.detailValue}>{item.category}</span>
            </div>
            {item.subcategory && (
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>{t('itemDetail.subcategory')}</span>
                <span className={styles.detailValue}>{item.subcategory}</span>
              </div>
            )}
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>{t('itemDetail.condition')}</span>
              <span className={styles.detailValue}>{item.condition}</span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>{t('itemDetail.quantity')}</span>
              <span className={styles.detailValue}>{item.quantity}</span>
            </div>
            {item.location && (
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>{t('itemDetail.location')}</span>
                <span className={styles.detailValue}>{item.location}</span>
              </div>
            )}
            {canEdit && item.acquisitionDate && (
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>{t('itemDetail.acquired')}</span>
                <span className={styles.detailValue}>
                  {new Date(item.acquisitionDate).toLocaleDateString()}
                </span>
              </div>
            )}
            {canEdit && item.acquisitionPrice && (
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>{t('itemDetail.price')}</span>
                <span className={styles.detailValue}>{item.currency} {item.acquisitionPrice}</span>
              </div>
            )}
            {item.ownerDisplayName && item.createdBy !== user?.userId && (
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>{t('itemDetail.owner')}</span>
                <span className={styles.detailValue}>
                  <Link
                    to="/profile/$slug"
                    params={{ slug: item.createdBy! }}
                    style={{ color: 'var(--accent)', textDecoration: 'none' }}
                  >
                    {item.ownerDisplayName}
                  </Link>
                  {item.ownerLocation && <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}> · {item.ownerLocation}</span>}
                </span>
              </div>
            )}
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>{t('itemDetail.added')}</span>
              <span className={styles.detailValue}>
                {new Date(item.createdAt).toLocaleDateString()}
              </span>
            </div>
            {(item as any).metadata?.cpu && (
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>{t('itemDetail.cpu')}</span>
                <span className={styles.detailValue}>{(item as any).metadata.cpu}</span>
              </div>
            )}
            {(item as any).metadata?.memory && (
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>{t('itemDetail.memory')}</span>
                <span className={styles.detailValue}>{(item as any).metadata.memory}</span>
              </div>
            )}
            {(item as any).metadata?.launchDate && (
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>{t('itemDetail.launchInfo')}</span>
                <span className={styles.detailValue}>{(item as any).metadata.launchDate}</span>
              </div>
            )}
            {(item as any).metadata?.launchPrice && (
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>{t('itemDetail.launchPrice')}</span>
                <span className={styles.detailValue}>
                  {(item as any).metadata.launchPriceCurrency ?? ''} {(item as any).metadata.launchPrice}
                  {(item as any).metadata.launchPriceInflationAdjusted && (
                    <span style={{ color: 'var(--text-muted)', marginLeft: '0.5rem' }}>
                      ({t('itemDetail.inflationAdjusted')}: ${(item as any).metadata.launchPriceInflationAdjusted})
                    </span>
                  )}
                </span>
              </div>
            )}
            {(item as any).metadata?.customNotes && (
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>{t('itemDetail.customNotes')}</span>
                <span className={styles.detailValue}>{(item as any).metadata.customNotes}</span>
              </div>
            )}
            {Array.isArray((item as any).metadata?.customFields) && (item as any).metadata.customFields.length > 0 && (
              (item as any).metadata.customFields.map((cf: { key: string; value: string }, i: number) => (
                <div className={styles.detailRow} key={i}>
                  <span className={styles.detailLabel}>{cf.key}</span>
                  <span className={styles.detailValue}>{cf.value}</span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Listing Status (owner only, web only) */}
        {canEdit && mode !== 'desktop' && (
          <div className={styles.listingCard}>
            <ListingStatusSelector
              itemId={itemId}
              currentStatus={item.listingStatus}
              currentPrice={item.askingPrice}
              currentCurrency={item.listingCurrency}
            />
          </div>
        )}

        {/* Favorite + Make Offer (non-owner) */}
        {!isOwner && item.ownerDisplayName && (
          <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <FavoriteButton itemId={itemId} />
            {item.listingStatus !== 'personal' && (
              <Button
                size="small"
                onClick={async () => {
                  try {
                    // Fetch owner's other listed items via their profile
                    const res = await apiClient.getProfileItems(
                      item.createdBy!,
                      { filter: 'all', limit: '100' }
                    );
                    const listed = (res.data ?? []).filter(i => i.listingStatus === 'for-trade' || i.listingStatus === 'for-sale');
                    // Always include the current item so it can be pre-selected
                    const hasCurrentItem = listed.some(i => i.id === item.id);
                    setOwnerListedItems(hasCurrentItem ? listed : [item, ...listed]);
                  } catch {
                    setOwnerListedItems([item]);
                  }
                  setShowOfferModal(true);
                }}
              >
                {t('community.makeOffer')}
              </Button>
            )}
          </div>
        )}

        {canEdit && item.description && (
          <div className={styles.description}>{item.description}</div>
        )}

        {canEdit && (
          <>
            {/* Feature 4: Peripheral Links */}
            <ItemLinks itemId={itemId} canEdit={canEdit} />

            {/* Feature 1: Capacitor Map / Recap Status */}
            <CapacitorList
              itemId={itemId}
              capacitors={capMapData?.capacitors ?? []}
              progress={capMapData?.progress ?? { total: 0, replaced: 0, percent: 0 }}
              hasMap={!!capMapData && !capMapData.statusCode}
              canEdit={canEdit}
            />

            {/* Feature 3: Retrobright */}
            <RetroBrightSection itemId={itemId} canEdit={canEdit} />

            {/* Feature 6: Maintenance Alerts */}
            <AlertList itemId={itemId} canEdit={canEdit} />
          </>
        )}

        {/* Feature 5: Library Files */}
        {canEdit && files.length > 0 && (
          <div style={{ marginTop: '1.5rem' }}>
            <h3 style={{ fontSize: '0.8125rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>
              {t('itemDetail.files')}
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
              {files.map((file: any) => (
                <a
                  key={file.id}
                  href={file.fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.5rem 0.75rem',
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius)',
                    textDecoration: 'none',
                    color: 'inherit',
                    fontSize: '0.8125rem',
                    fontFamily: 'var(--font-mono)',
                  }}
                >
                  <span style={{ color: 'var(--accent)' }}>
                    [{file.fileType.replace('_', ' ').toUpperCase()}]
                  </span>
                  <span>{file.name}</span>
                </a>
              ))}
            </div>
          </div>
        )}

        <Modal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          title={t('itemDetail.deleteItemTitle')}
          footer={
            <>
              <Button
                variant="secondary"
                onClick={() => setShowDeleteModal(false)}
              >
                {t('itemDetail.cancel')}
              </Button>
              <Button
                variant="danger"
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? t('itemDetail.deleting') : t('itemDetail.delete')}
              </Button>
            </>
          }
        >
          <p>
            {t('itemDetail.deleteConfirm')} <strong>{item.name}</strong>? {t('itemDetail.deleteWarning')}
          </p>
        </Modal>

        {lightboxIndex !== null && (imagesData?.data ?? []).length > 0 && (
          <ImageLightbox
            images={imagesData?.data ?? []}
            currentIndex={lightboxIndex}
            onIndexChange={setLightboxIndex}
            onClose={() => setLightboxIndex(null)}
            title={item.name}
            canEdit={canEdit}
            onSetPrimary={(imageId) => {
              setPrimaryMutation.mutate({ itemId, imageId });
            }}
            onDelete={(imageId) => {
              deleteImageMutation.mutate({ itemId, imageId }, {
                onSuccess: () => toast(t('imageGallery.deleteImage'), 'success'),
                onError: () => toast(t('itemDetail.imageUploadFailed'), 'error'),
              });
            }}
          />
        )}

        {editorImageSrc && (
          <ImageEditor
            imageSrc={editorImageSrc}
            onSave={handleEditorSave}
            onCancel={handleEditorCancel}
            isSaving={uploadMultiMutation.isPending || deleteImageMutation.isPending}
          />
        )}

        {showOfferModal && (
          <CreateOfferForm
            isOpen={showOfferModal}
            onClose={() => setShowOfferModal(false)}
            toUserId={item.createdBy!}
            toUserName={item.ownerDisplayName || ''}
            targetItems={ownerListedItems}
            ownItems={(ownItemsData?.data ?? []).filter(i => i.listingStatus === 'for-trade' || i.listingStatus === 'for-sale')}
            preSelectedItemId={item.id}
          />
        )}

        {showReviewModal && analysisResult && (
          <AiReviewModal
            isOpen={showReviewModal}
            onClose={() => { setShowReviewModal(false); setAnalysisResult(null); }}
            currentItem={item}
            suggestions={analysisResult}
            onAccept={handleAcceptAnalysis}
            isApplying={updateMutation.isPending}
          />
        )}
      </div>
    </Shell>
  );
}
